import { TranslateService } from '@ngx-translate/core';

import { FilterVocabulary } from '@core/filters/path-filter/vocabulary/vocabulary';

import {
  PhraseSegment,
  describeConditions,
  describeFilter
} from '@shared/components/path-filter/phrase/describe-filter';
import { AvailableLanguages } from '@shared/types/translation.type';

import { RequirementDraft, printConditionText } from './requirement-draft';

const PHRASE_I18N = 'form.compatibility.phrase';

export interface RequirementVocabularies {
  filter: FilterVocabulary;
  context: FilterVocabulary;
}

function plain(text: string): PhraseSegment {
  return { text, role: 'plain', node: null };
}

/**
 * The requirement as one sentence: whether it is hard or soft, what the target must satisfy, and
 * the conditions under which it applies. Built on the filter phrase, so a requirement reads like
 * the filters it is made of.
 */
export function describeRequirement(
  draft: RequirementDraft,
  vocabularies: RequirementVocabularies,
  lang: AvailableLanguages,
  translate: TranslateService,
  selfLabel: string | null
): PhraseSegment[] {
  const t = (key: string, params?: object): string =>
    translate.instant(`${PHRASE_I18N}.${key}`, params);
  const lead = !draft.soft
    ? t('hard')
    : draft.penalty === null
      ? t('soft_unset')
      : t('soft', { penalty: draft.penalty });
  const when = conditionParts(draft, vocabularies.context, lang, translate, t);
  return [
    { text: lead, role: 'field', node: null },
    plain(' '),
    ...describeFilter(draft.filter, vocabularies.filter, lang, translate, selfLabel),
    ...(when.length === 0
      ? []
      : [
          plain(`; ${t('only_when')} `),
          ...when.flatMap((part, index) =>
            index === 0
              ? part
              : [
                  plain(
                    ` ${translate.instant('features.data.path_filter.phrase.words.and')} `
                  ),
                  ...part
                ]
          )
        ])
  ];
}

/** Each condition the requirement applies under, as the panel's controls hold it. */
function conditionParts(
  draft: RequirementDraft,
  contextVocabulary: FilterVocabulary,
  lang: AvailableLanguages,
  translate: TranslateService,
  t: (key: string, params?: object) => string
): PhraseSegment[][] {
  const parts: PhraseSegment[][] = [];
  const context = describeConditions(draft.context, contextVocabulary, lang, translate);
  if (context.length > 0) parts.push(context);
  const positions = (['first', 'last'] as const).filter((position) =>
    draft.positions.includes(position)
  );
  if (positions.length > 0) parts.push([plain(t(`position_${positions.join('_')}`))]);
  const window = draft.serviceWindow;
  if (window && window.start !== '' && window.end !== '') {
    parts.push([plain(t('service_window', { start: window.start, end: window.end }))]);
  }
  for (const { condition } of draft.kept) {
    parts.push([{ text: printConditionText(condition), role: 'value', node: null }]);
  }
  return parts;
}
